import {
  BufferGeometry,
  Float32BufferAttribute,
  Uint32BufferAttribute,
} from "three";

// 共有 IFC ローダ + パース結果キャッシュ。
//
// 目的: サムネイル (IfcThumbnail) と配置プレースホルダー (DesignManager) が
// それぞれ独自に worker を生成して IFC をダウンロード / パースしていたため、
// - サムネイルがスクロールで再生成されるたびに WASM を初期化し直す
// - 同じ IFC を複数箇所で重複してダウンロード / パースする
// といった無駄が発生し、描画がカクついていた。
//
// ここでは
//  1) WASM 初期化済みの worker を 1 つだけ常駐させて使い回す
//  2) URL 単位でパース済みメッシュデータを Promise でキャッシュする
// ことで、同一 IFC の再ダウンロード / 再パース / WASM 再初期化を防ぐ。

export type IfcMeshData = {
  vertices: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  translation: number[];
  color?: { r: number; g: number; b: number; a: number };
};

// 常駐 worker と初期化 (ready) の状態。
let worker: Worker | null = null;
let ready: Promise<void> | null = null;
let nextId = 1;

// postMessage の id と解決待ち Promise の対応表。
const pending = new Map<
  number,
  { resolve: (d: IfcMeshData[]) => void; reject: (e: Error) => void }
>();

// URL → パース結果 (in-flight 中も含む) のキャッシュ。
// 同じ URL を同時に要求しても worker への送信は 1 回で済む。
const cache = new Map<string, Promise<IfcMeshData[]>>();
const MAX_CACHE = 24; // LRU 上限 (メモリ保護)

// WASM 初期化済みの worker を 1 つだけ用意する。
function ensureWorker(): Promise<void> {
  if (worker && ready) return ready;

  ready = new Promise<void>((resolve, reject) => {
    let w: Worker;
    try {
      w = new Worker("/ifc-load-worker.js");
    } catch (e: any) {
      reject(new Error(e?.message ?? "Failed to create IFC worker"));
      return;
    }
    worker = w;

    w.onmessage = ({ data }) => {
      if (data?.type === "ready") {
        resolve();
        return;
      }
      if (data?.type === "error") {
        // WASM 初期化失敗。次回再試行できるよう worker を破棄する。
        reject(new Error(data.error ?? "IFC worker init failed"));
        worker = null;
        ready = null;
        return;
      }
      // 個別リクエストの応答 ({ id, data } または { id, error })。
      const entry = pending.get(data?.id);
      if (!entry) return;
      pending.delete(data.id);
      if (data.error || !data.data) {
        entry.reject(new Error(data.error ?? "IFC load failed"));
      } else {
        entry.resolve(data.data as IfcMeshData[]);
      }
    };

    w.onerror = (e) => reject(new Error(e.message || "IFC worker error"));
  });

  return ready;
}

/**
 * 指定 URL の IFC をパースしてメッシュデータ配列を返す。
 * 同じ URL は一度だけダウンロード / パースされ、以降はキャッシュを返す。
 */
export function loadIfcMesh(url: string): Promise<IfcMeshData[]> {
  const cached = cache.get(url);
  if (cached) {
    // LRU: 参照したものを末尾へ移動。
    cache.delete(url);
    cache.set(url, cached);
    return cached;
  }

  const p = ensureWorker().then(
    () =>
      new Promise<IfcMeshData[]>((resolve, reject) => {
        const id = nextId++;
        pending.set(id, { resolve, reject });
        worker!.postMessage({ id, url });
      }),
  );

  // 失敗時はキャッシュから外し、次回再試行できるようにする。
  p.catch(() => {
    if (cache.get(url) === p) cache.delete(url);
  });

  cache.set(url, p);
  if (cache.size > MAX_CACHE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return p;
}

/**
 * worker が返すメッシュ配列を 1 つの BufferGeometry にマージする。
 * (IFC Y-up -> Z-up に回転し、頂点カラーを最低輝度でクランプする)
 *
 * 注意: WebGL コンテキストごとに GPU バッファが必要なため、ジオメトリ自体は
 * 共有せず、消費側がこの関数で都度生成する (キャッシュ対象は CPU 側の
 * メッシュデータのみ)。
 */
export function buildIfcGeometry(
  meshData: IfcMeshData[],
  minBrightness = 0.4,
): BufferGeometry {
  let totalV = 0;
  let totalI = 0;
  for (const d of meshData) {
    totalV += d.vertices.length / 3;
    totalI += d.indices.length;
  }
  const positions = new Float32Array(totalV * 3);
  const normals = new Float32Array(totalV * 3);
  const colors = new Float32Array(totalV * 3);
  const indices = new Uint32Array(totalI);

  let vo = 0;
  let io = 0;
  const base = meshData[0]?.translation || [0, 0, 0];

  for (const d of meshData) {
    const vc = d.vertices.length / 3;
    const t = d.translation || [0, 0, 0];
    const dx = t[0] - base[0];
    const dy = t[1] - base[1];
    const dz = t[2] - base[2];
    for (let k = 0; k < vc; k++) {
      positions[(vo + k) * 3] = d.vertices[k * 3] + dx;
      positions[(vo + k) * 3 + 1] = d.vertices[k * 3 + 1] + dy;
      positions[(vo + k) * 3 + 2] = d.vertices[k * 3 + 2] + dz;
    }
    normals.set(d.normals, vo * 3);
    for (let j = 0; j < d.indices.length; j++) {
      indices[io + j] = d.indices[j] + vo;
    }
    const r = Math.max(d.color?.r ?? 0.5, minBrightness);
    const g = Math.max(d.color?.g ?? 0.5, minBrightness);
    const b = Math.max(d.color?.b ?? 0.5, minBrightness);
    for (let j = 0; j < vc; j++) {
      colors[(vo + j) * 3] = r;
      colors[(vo + j) * 3 + 1] = g;
      colors[(vo + j) * 3 + 2] = b;
    }
    vo += vc;
    io += d.indices.length;
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("normal", new Float32BufferAttribute(normals, 3));
  geo.setAttribute("color", new Float32BufferAttribute(colors, 3));
  geo.setIndex(new Uint32BufferAttribute(indices, 1));
  geo.rotateX(Math.PI / 2); // IFC Y-up -> Z-up
  return geo;
}
